'use client';

/**
 * BottomSheetShell — presentational chrome shared by the cookie-consent
 * banner and the chat-context tool-approval (CriticalToast) prompt.
 *
 * Both surfaces want the same visual language — translucent / glassy
 * card, mobile drag handle, safe-area padding, slide-in-from-bottom
 * animation — but differ in their interaction model:
 *
 *   - CookieConsentBanner uses {@link BottomSheet} (open/close + drag-to-
 *     dismiss). It composes `BottomSheetShell` internally so the visual
 *     chrome stays in one place.
 *   - CriticalToast (tool_approval branch) renders an `alertdialog` with
 *     a focus trap and cannot reuse the higher-level `BottomSheet`
 *     wrapper directly (it manages mounting / focus itself), so it
 *     wraps its content with `BottomSheetShell` to inherit the same
 *     visuals.
 *
 * The shell is deliberately layout-agnostic — it does NOT position
 * itself or render a backdrop; callers wrap it in their own positioning
 * container (e.g. `fixed bottom-4 left-4` for desktop banner, or a
 * full-width fixed wrapper for the modal toast).
 */

import * as React from 'react';

// Sheet surface — used by every bottom-anchored prompt: passkey,
// cookie consent, push prompt, Discord install continue, and the
// critical toast. Keeps them all on the same glassy chrome so the
// "small system nudge" lane reads as one family. Opacity bumped
// from the original 85/90 to 94/95 so the card sits more solidly
// against the workspace (the previous values felt washed out
// against the agent canvas behind them) while keeping the
// backdrop-blur for the glass effect.
const sheetSurfaceClassName = [
  'relative overflow-hidden border border-black/10 bg-popover/98 text-popover-foreground',
  'shadow-[0_24px_80px_rgba(15,23,42,0.18),0_1px_0_rgba(255,255,255,0.65)_inset]',
  'backdrop-blur-xl supports-[backdrop-filter]:bg-popover/94',
  'dark:border-white/10 dark:bg-zinc-950/98 dark:supports-[backdrop-filter]:bg-zinc-950/95',
  'dark:shadow-[0_28px_90px_rgba(0,0,0,0.72),0_1px_0_rgba(255,255,255,0.06)_inset]',
].join(' ');

export interface BottomSheetShellProps extends React.HTMLAttributes<HTMLDivElement> {
  /** When true, renders the centered drag handle at the top (mobile sheets, tool-approval prompts). */
  showHandle?: boolean;
  /**
   * Corner radius / border treatment:
   *   - `'rounded'` (default): fully rounded card, all borders. Desktop banner cards.
   *   - `'docked'`: rounded top only, no x/bottom borders. Mobile sheet glued to viewport bottom.
   *   - `'docked-sm-rounded'`: docked on mobile, fully rounded card from `sm:` up. Used for
   *     the chat-context tool-approval modal which renders bottom-anchored on mobile but as
   *     a centered card on desktop.
   */
  radiusMode?: 'rounded' | 'docked' | 'docked-sm-rounded';
  /** Apply env(safe-area-inset-bottom) padding so the sheet sits above the iOS home indicator. */
  safeAreaBottom?: boolean;
  /** Show the drag handle only on mobile (`< sm`). Used by tool-approval modal. */
  handleMobileOnly?: boolean;
  /** Replace the default surface classes with caller-supplied chrome (e.g. amber accent). */
  surfaceClassName?: string;
  /** Padding override for the inner content area. Defaults to `p-4`. */
  contentClassName?: string;
}

export const BottomSheetShell = React.forwardRef<HTMLDivElement, BottomSheetShellProps>(
  function BottomSheetShell(
    {
      showHandle = false,
      handleMobileOnly = false,
      radiusMode = 'rounded',
      safeAreaBottom = false,
      contentClassName = 'p-4',
      surfaceClassName,
      className = '',
      children,
      ...rest
    },
    ref,
  ) {
    const radiusClass =
      radiusMode === 'docked'
        ? 'rounded-t-2xl border-x-0 border-b-0'
        : radiusMode === 'docked-sm-rounded'
          ? 'rounded-t-2xl sm:rounded-2xl'
          : 'rounded-2xl';
    const safeAreaClass = safeAreaBottom ? 'pb-[env(safe-area-inset-bottom)]' : '';
    const surface = surfaceClassName ?? sheetSurfaceClassName;
    const handleVisibility = handleMobileOnly ? 'sm:hidden' : '';
    return (
      <div
        ref={ref}
        className={`${surface} ${radiusClass} ${safeAreaClass} ${className}`.trim()}
        {...rest}
      >
        {showHandle && (
          <div className={`flex justify-center pt-3 pb-1 ${handleVisibility}`.trim()}>
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>
        )}
        <div className={contentClassName}>{children}</div>
      </div>
    );
  },
);
